<script setup lang="ts">
import type { TaskCompletionStatus, TaskFilterStatus } from '~/utils/reportUtils'
import type { ReportFilters } from '~/models/ReportFilters'
import type { ContentDetailsTest, FlytItemSetResult } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSort, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons'
import { getTaskCompletionStatus, resolveUserName } from '~/utils/reportUtils'
import { getTaskStatusIcon } from '~/utils/reportIcons'
import useGroupsStore from '~/stores/groups'
import { UserRole } from '~/models/User/UserRole'
import { useTime } from '~/composables/useTime'

const props = defineProps<{
  filters?: ReportFilters
  test: ContentDetailsTest
  statusFilter: TaskFilterStatus
}>()

const { t } = useI18n()
const { formatReportTimestamp } = useTime()

interface ProcessedStudentData {
  userId: string
  firstName: string
  lastName: string
  status: TaskCompletionStatus
  lastActivity: string | null
  items: Array<{ index: number, result: FlytItemSetResult, score: number, targetScore: number }>
  groupMember?: GroupMember
  hasTasks: boolean
}

const getStudentName = (userId: string): string => {
  let groupMember: GroupMember | undefined

  if (props.filters?.selectedGroup?.groupId) {
    const groupsStore = useGroupsStore()
    groupMember = groupsStore.findGroupMembersById(props.filters.selectedGroup.groupId)
      .find(member => member.userId === userId)
  }

  const userActivity = props.test.testActivities.find(activity => String(activity?.user?.userId) === String(userId))

  return resolveUserName(groupMember, userActivity?.user)
}

function getStudentStatus(groupMember: GroupMember): TaskCompletionStatus {
  const userActivity = props.test.testActivities.find(activity => String(activity?.user?.userId) === String(groupMember.userId))

  if (!userActivity) {
    return 'notStarted'
  }

  const userTaskData = {
    userId: String(groupMember.userId),
    status: 'notStarted' as const,
    items: userActivity.items,
    lastActivity: userActivity.providedTimestamp,
    attempts: 0
  }

  return getTaskCompletionStatus(userTaskData)
}

const taskIds = computed(() => {
  let maxTaskCount = 0

  props.test.testActivities.forEach((activity) => {
    if (activity.items && activity.items.length > 0) {
      maxTaskCount = Math.max(maxTaskCount, activity.items.length)
    }
  })

  return Array.from({ length: maxTaskCount || 5 }, (_, i) => i + 1)
})

const allStudentsData = computed((): ProcessedStudentData[] => {
  if (!props.filters?.selectedGroup?.groupId) {
    return []
  }

  const groupsStore = useGroupsStore()
  const groupMembers = groupsStore.findGroupMembersById(props.filters.selectedGroup.groupId)

  return groupMembers
    .filter(member => member.role !== UserRole.Teacher)
    .filter((member) => {
      if (props.statusFilter === 'all')
        return true
      const studentStatus = getStudentStatus(member)
      return studentStatus === props.statusFilter
    })
    .map((groupMember) => {
      const userActivity = props.test.testActivities.find(activity => String(activity?.user?.userId) === String(groupMember.userId))

      if (!userActivity || !userActivity.items || userActivity.items.length === 0) {
        return {
          userId: String(groupMember.userId),
          firstName: groupMember.firstName || '',
          lastName: groupMember.lastName || '',
          status: 'notStarted' as const,
          lastActivity: null,
          items: [],
          groupMember,
          hasTasks: false
        }
      }
      const items = userActivity.items
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((result, index) => ({
          index,
          result: result.result,
          score: result.result === 'CORRECT' ? result.targetScore : result.result === 'PARTIALLY_CORRECT' ? result.score : 0,
          targetScore: result.targetScore,
        }))

      const userTaskData = {
        userId: String(groupMember.userId),
        status: 'notStarted' as const,
        items: userActivity.items,
        lastActivity: userActivity.providedTimestamp,
        attempts: 0
      }

      return {
        userId: String(groupMember.userId),
        firstName: userActivity.user?.firstName || groupMember.firstName || '',
        lastName: userActivity.user?.lastName || groupMember.lastName || '',
        status: getTaskCompletionStatus(userTaskData),
        lastActivity: userActivity.providedTimestamp,
        items,
        groupMember,
        hasTasks: true
      }
    })
})

type SortField = 'name' | 'date' | null
type SortDirection = 'asc' | 'desc'

const sortField = ref<SortField>()
const sortDirection = ref<SortDirection>('asc')

const toggleSort = (field: SortField) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortDirection.value = 'asc'
  }
}

const getSortIcon = (field: SortField) => sortField.value !== field ? faSort : (sortDirection.value === 'asc' ? faSortUp : faSortDown)

const scrollHintText = computed(() => t('reports.table.scrollHint'))
const nameHeaderText = computed(() => t('reports.table.headers.name'))
const progressionHeaderText = computed(() => t('reports.table.headers.progression'))
const statusHeaderText = computed(() => t('reports.formwriter.status'))
const lastChangedHeaderText = computed(() => t('reports.table.headers.lastVisited'))
const dateHeaderText = computed(() => t('reports.table.headers.date'))

const getDisplayStatus = (student: ProcessedStudentData) => {
  if (!student.hasTasks) {
    return t('reports.status.notStarted')
  }
  return student.status === 'completed' ? t('reports.status.completed') : t('reports.status.started')
}

const getFormattedTimestamp = (lastActivity: string | null) => formatReportTimestamp(lastActivity) || '-'
const getShortTimestamp = (lastActivity: string | null) => lastActivity ? formatReportTimestamp(lastActivity)?.split(' ')[0] || '-' : '-'

const sortedStudents = computed(() => {
  const students = [...allStudentsData.value]

  if (!sortField.value)
    return students

  return students.sort((a, b) => {
    let aValue: string | number
    let bValue: string | number

    if (sortField.value === 'name') {
      aValue = getStudentName(a.userId).toLowerCase()
      bValue = getStudentName(b.userId).toLowerCase()
    } else if (sortField.value === 'date') {
      if (!a.lastActivity && !b.lastActivity)
        return 0
      if (!a.lastActivity)
        return 1
      if (!b.lastActivity)
        return -1

      aValue = new Date(a.lastActivity).getTime()
      bValue = new Date(b.lastActivity).getTime()
    } else {
      return 0
    }

    const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
    return sortDirection.value === 'asc' ? comparison : -comparison
  })
})

const getTaskScoreIcon = (student: ProcessedStudentData, taskId: number) => {
  if (!student.hasTasks) {
    return getTaskStatusIcon(null, t)
  }

  const taskIndex = taskId - 1
  const item = student.items.find(item => item.index === taskIndex)

  if (!item) {
    return getTaskStatusIcon(null, t)
  }

  return getTaskStatusIcon(item.result, t, item.score, item.targetScore)
}
const scrollContainer = ref<HTMLElement | null>()
const tableContent = ref<HTMLElement | null>()
const showScrollHint = ref(false)

const checkScrollNeeded = () => {
  if (scrollContainer.value && tableContent.value) {
    const containerWidth = scrollContainer.value.clientWidth
    const contentWidth = tableContent.value.scrollWidth
    showScrollHint.value = contentWidth > containerWidth
  }
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  checkScrollNeeded()

  if (typeof ResizeObserver !== 'undefined' && scrollContainer.value) {
    resizeObserver = new ResizeObserver(() => {
      checkScrollNeeded()
    })
    resizeObserver.observe(scrollContainer.value)
  }

  window.addEventListener('resize', checkScrollNeeded)
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
  window.removeEventListener('resize', checkScrollNeeded)
})

watch(taskIds, () => {
  nextTick(() => {
    checkScrollNeeded()
  })
})
</script>

<template>
  <div class="space-y-2">
    <div v-if="showScrollHint" class="bg-green-40 border-green-40 rounded-md border px-3 py-2 text-sm text-white">
      <div class="flex items-center justify-center gap-2">
        <svg
          class="h-4 w-4 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span v-text="scrollHintText" />
      </div>
    </div>

    <div class="border-green-40 overflow-hidden rounded-lg border bg-white shadow-sm">
      <div ref="scrollContainer" class="overflow-x-auto">
        <table
          ref="tableContent"
          class="w-full border-collapse"
          :style="{ minWidth: `${Math.max(600, 400 + taskIds.length * 80)}px` }"
        >
          <thead class="bg-green-30">
            <tr>
              <th class="border-green-40 border-r p-4 text-left">
                <button
                  type="button"
                  class="flex items-center gap-1 text-left text-xs font-medium tracking-wider text-white uppercase transition-colors hover:text-white/80 focus-visible:ring sm:gap-2"
                  @click="toggleSort('name')"
                >
                  <span class="truncate" v-text="nameHeaderText" />
                  <FontAwesomeIcon
                    :icon="getSortIcon('name')"
                    class="h-3 w-3 flex-shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </th>

              <th class="border-green-40 border-r p-4 text-left text-xs font-medium tracking-wider text-white uppercase">
                <span class="hidden sm:inline" v-text="progressionHeaderText" />
                <span class="sm:hidden" v-text="statusHeaderText" />
              </th>

              <th class="border-green-40 border-r p-4 text-left">
                <button
                  type="button"
                  class="flex items-center gap-1 text-xs font-medium tracking-wider text-white uppercase transition-colors hover:text-white/80 focus-visible:ring sm:gap-2"
                  @click="toggleSort('date')"
                >
                  <span class="hidden sm:inline" v-text="lastChangedHeaderText" />
                  <span class="sm:hidden" v-text="dateHeaderText" />
                  <FontAwesomeIcon
                    :icon="getSortIcon('date')"
                    class="h-3 w-3 flex-shrink-0"
                    aria-hidden="true"
                  />
                </button>
              </th>
              <th
                v-for="(taskId, index) in taskIds"
                :key="taskId"
                class="p-4 text-left text-xs font-medium tracking-wider text-white uppercase"
                :class="{ 'border-green-40 border-r': index < taskIds.length - 1 }"
              >
                <span v-text="taskId" />
              </th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="(student, index) in sortedStudents"
              :key="student.userId"
              class="hover:bg-gray-2 transition"
              :class="[
                index !== sortedStudents.length - 1 ? 'border-green-40 border-b' : '',
              ]"
            >
              <td class="border-green-40 border-r p-4">
                <div class="text-green-40 text-sm font-medium">
                  <p class="break-words" v-text="getStudentName(student.userId)" />
                </div>
              </td>

              <td class="border-green-40 border-r p-4 text-left">
                <div class="flex items-center gap-2">
                  <span class="text-green-40 text-sm font-medium" v-text="getDisplayStatus(student)" />
                </div>
              </td>

              <td class="border-green-40 text-gray-60 border-r p-4 text-left text-xs sm:text-sm">
                <span class="hidden sm:inline" v-text="getFormattedTimestamp(student.lastActivity)" />
                <span class="sm:hidden" v-text="getShortTimestamp(student.lastActivity)" />
              </td>
              <td
                v-for="(taskId, taskIndex) in taskIds"
                :key="taskId"
                class="p-4 text-left"
                :class="{ 'border-green-40 border-r': taskIndex < taskIds.length - 1 }"
              >
                <FontAwesomeIcon
                  :icon="getTaskScoreIcon(student, taskId).icon"
                  :class="getTaskScoreIcon(student, taskId).class"
                  :title="getTaskScoreIcon(student, taskId).title"
                  class="h-6 w-6 text-xl sm:h-8 sm:w-8 sm:text-2xl"
                  aria-hidden="true"
                />
                <span class="sr-only" v-text="getTaskScoreIcon(student, taskId).title" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
